import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { TrendingSidebar } from '@/components/TrendingSidebar'
import { SwipeableTabContent } from '@/components/SwipeableTabContent'
import { useQueryClient } from '@tanstack/react-query'
import { RefreshCw, Settings, SlidersHorizontal } from 'lucide-react'
import { NewPostsPill } from '@/components/NewPostsPill'
import { useNewPostsDetection } from '@/hooks/useNewPostsDetection'
import { PostList } from '@/components/PostList'
import { ReplyModal } from '@/components/ReplyModal'
import { QuoteModal } from '@/components/QuoteModal'
import { PullToRefresh } from '@/components/PullToRefresh'
import { useAdapter } from '@/hooks/useAdapter'
import { useAuth } from '@/hooks/useAuth'
import { useProtocol } from '@/hooks/useProtocol'
import { useScrollDirection } from '@/hooks/useScrollDirection'
import { useTimeline } from '@/hooks/useTimeline'
import { useCrossProtocolTimeline } from '@/hooks/useCrossProtocolTimeline'
import { useMixedTimeline } from '@/hooks/useMixedTimeline'
import { useMastodonHashtagAttribution } from '@/hooks/useMastodonHashtagAttribution'
import { useSavedFeeds } from '@/hooks/useSavedFeeds'
import { usePostEngagement } from '@/hooks/usePostEngagement'
import { useReplyModal } from '@/hooks/useReplyModal'
import { useQuoteModal } from '@/hooks/useQuoteModal'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useCrossProtocol } from '@/contexts/CrossProtocolContext/CrossProtocolContext'
import { CrossProtocolSettings } from '@/components/CrossProtocolSettings/CrossProtocolSettings.component'
import { useMackinacTabPrefs, ARTICLES_TAB_ID } from '@/hooks/useMackinacTabPrefs'
import { useScopedSelectedFeed } from '@/hooks/useScopedSelectedFeed'
import { buildPostRouteFromUri } from '@/lib/apUtils'
import { APP_HEADER_HEIGHT_PX } from '@/lib/layout'
import { getMastodonAccountId } from '@/lib/mastodon'
import {
    MIXED_FEED_STORAGE_KEY,
    parseMixedFeedStorage,
    type MixedFeedState,
} from '@/lib/mixedFeedStorage'
import { resolveEffectiveMixFeeds } from '@/lib/mixedFeedSampling'
import type { Post } from '@/types/posts'
import { tabIdToTag } from '@/services/userPreferencesService'
import { strings } from '@/strings'

const FEED_STORAGE_KEY = 'mackinac:selectedFeed'

export function Home({ isVisible }: { isVisible?: boolean }) {
    const navigate = useNavigate()
    const {
        session,
        mastodonSession,
        pixelfedSession,
        nostrSession,
        currentMastodonHandle,
        currentPixelfedHandle,
    } = useAuth()
    const { selectedProtocol, canCrossProtocol } = useProtocol()
    const { session: adapterSession, adapter } = useAdapter()
    const queryClient = useQueryClient()

    const isMastodonProtocol = selectedProtocol === 'mastodon'

    const { feeds, allFeeds, isLoading: isFeedsLoading, isReady: isFeedsReady } = useSavedFeeds()

    // Two independent selection tracks: legacy (Bluesky/Pixelfed/Nostr, unscoped key,
    // unchanged behavior) and Mastodon (account-scoped key, so switching accounts on the
    // same instance doesn't leak the previous account's selection). Only one is "active"
    // at a time, chosen by protocol below — both are called unconditionally since hooks
    // can't be called conditionally. Startup selection is always "last-selected", falling
    // back to Following — there is no default-tab concept, consistent with Bluesky.
    const legacySelection = useScopedSelectedFeed(FEED_STORAGE_KEY)
    const mastodonScope = mastodonSession ? getMastodonAccountId(mastodonSession) : null
    const mastodonSelection = useScopedSelectedFeed(
        mastodonScope ? `${FEED_STORAGE_KEY}:${mastodonScope}` : null
    )
    const activeSelection = isMastodonProtocol ? mastodonSelection : legacySelection
    const selectedFeedUri = activeSelection.selectedFeedUri
    const setSelectedFeedUri = activeSelection.setSelectedFeedUri

    const [mixFeedStorageState, setMixFeedStorageState] = useState<MixedFeedState>(() => {
        try {
            return parseMixedFeedStorage(localStorage.getItem(MIXED_FEED_STORAGE_KEY))
        } catch {
            return parseMixedFeedStorage(null)
        }
    })

    // Sync mix state when Feeds settings page changes it (same-tab storage changes don't
    // fire the native storage event, so Feeds dispatches a synthetic one after writing).
    useEffect(() => {
        const handleStorage = (e: StorageEvent) => {
            if (e.key !== MIXED_FEED_STORAGE_KEY) return
            setMixFeedStorageState(parseMixedFeedStorage(e.newValue))
        }
        window.addEventListener('storage', handleStorage)
        return () => window.removeEventListener('storage', handleStorage)
    }, [])

    const { showAllAccounts, setShowAllAccounts, enabledAccountIds } = useCrossProtocol()
    const [settingsOpen, setSettingsOpen] = useState(false)

    const { scrollDirection, isAtTop } = useScrollDirection(10, APP_HEADER_HEIGHT_PX)

    // Feed selection persistence is now owned by useScopedSelectedFeed (called above for
    // both tracks) — each writes to its own key on setSelectedFeedUri, so no separate
    // persistence effect is needed here.

    // Reset feed selection when switching protocols or accounts (skip initial mount).
    // Mastodon's activeIdentifier is the full account scope (getMastodonAccountId) rather
    // than instance_url, so switching between two accounts on the same instance is detected
    // too. For Mastodon, the scoped selection hook already resets/re-hydrates the selection
    // when its storage key changes — calling setSelectedFeedUri(undefined) here as well
    // would clobber the just-hydrated per-account selection with Following, so it's skipped
    // for that protocol.
    const activeIdentifier =
        selectedProtocol === 'mastodon'
            ? (mastodonScope ?? undefined)
            : selectedProtocol === 'pixelfed'
              ? pixelfedSession?.instance_url
              : selectedProtocol === 'nostr'
                ? nostrSession?.pubkey
                : session?.did
    const prevIdentifierRef = useRef(activeIdentifier)
    useEffect(() => {
        if (
            prevIdentifierRef.current !== activeIdentifier &&
            prevIdentifierRef.current !== undefined
        ) {
            prevIdentifierRef.current = activeIdentifier
            if (!isMastodonProtocol) {
                setSelectedFeedUri(undefined)
            }
            setShowAllAccounts(false)
            window.scrollTo({ top: 0 })
        } else {
            prevIdentifierRef.current = activeIdentifier
        }
    }, [activeIdentifier, isMastodonProtocol, setSelectedFeedUri, setShowAllAccounts])

    // Clear persisted feed if it's no longer in the user's saved feeds. For Mastodon,
    // this only runs once the selection is hydrated and lists are authoritative
    // (isReady === lists isSuccess, never !isLoading) — otherwise a transient lists
    // fetch error would wipe a persisted mastodon-list:{id} selection (it briefly
    // disappears from `feeds` while lists are non-authoritative, see useSavedFeeds).
    useEffect(() => {
        if (!selectedFeedUri) return
        // Hashtag tabs are not Bluesky saved feeds — skip validation for them
        if (tabIdToTag(selectedFeedUri) !== null) return
        if (isMastodonProtocol) {
            if (!mastodonSelection.isHydrated || !isFeedsReady) return
        } else if (isFeedsLoading) {
            return
        }
        const isValid = feeds.some(f => f.uri === selectedFeedUri)
        if (!isValid) {
            setSelectedFeedUri(undefined)
        }
    }, [
        feeds,
        isFeedsLoading,
        isFeedsReady,
        isMastodonProtocol,
        mastodonSelection.isHydrated,
        selectedFeedUri,
        setSelectedFeedUri,
    ])

    const {
        flags: { crossProtocol: crossProtocolEnabled },
    } = useFeatureFlags()

    // Build Bluesky saved feed items for reconciliation (pinned feeds from savedFeedsPrefV2)
    const bskyItemsForPrefs = useMemo(
        () =>
            allFeeds
                .filter(f => f.id != null && f.type != null)
                .map(f => ({
                    id: f.id!,
                    type: f.type!,
                    value: f.value ?? f.uri,
                    pinned: f.pinned ?? false,
                })),
        [allFeeds]
    )

    const { unifiedTabs: mackinacUnifiedTabs } = useMackinacTabPrefs({
        bskyItems: bskyItemsForPrefs,
        resolvedFeeds: allFeeds,
    })

    const isOnFollowingFeed = !selectedFeedUri
    const isCrossProtocol =
        canCrossProtocol && isOnFollowingFeed && showAllAccounts && crossProtocolEnabled

    const standardTimeline = useTimeline(selectedFeedUri, { enabled: !isCrossProtocol })
    const crossTimeline = useCrossProtocolTimeline({ enabled: isCrossProtocol, enabledAccountIds })

    // Pinned custom feeds for mixing into the Following timeline (also used for tab list)
    const pinnedCustomFeeds = useMemo(() => feeds.filter(f => f.uri !== 'following'), [feeds])

    // Feeds the user has selected to mix in.
    // When enabled=false, no feeds are mixed regardless of per-feed selections.
    // When feeds=null (legacy default), every pinned feed is used (resolveEffectiveMixFeeds —
    // no cap; mixedFeedSupply bounds how many are fetched at once, #2932).
    const selectedMixFeeds = useMemo(() => {
        if (!mixFeedStorageState.enabled) return []
        return resolveEffectiveMixFeeds(pinnedCustomFeeds, mixFeedStorageState.feeds)
    }, [mixFeedStorageState, pinnedCustomFeeds])

    /**
     * Unified tab list for the Home page tab bar.
     * For Bluesky: use the mackinac unified tabs (reconciled, filtered to visible only).
     * For other protocols: use the legacy feed list.
     */
    const unifiedTabs = useMemo(() => {
        if (selectedProtocol === 'bluesky' && mackinacUnifiedTabs.length > 0) {
            // Use mackinac source-of-truth: filter to visible tabs only. The ARTICLES_TAB_ID
            // check is a defensive boundary filter — Articles moved to its own bottom-nav route
            // (issue #2393) and must never render as a Home feed tab even if a legacy stored
            // { id: 'articles', visible: true } entry somehow reaches this memo.
            return mackinacUnifiedTabs
                .filter(t => t.visible && t.id !== ARTICLES_TAB_ID)
                .map(t => ({ id: t.id, label: t.label }))
        }
        // Non-Bluesky or no prefs yet: fall back to legacy tab list
        const tabs: { id: string; label: string }[] = [
            {
                id: 'following',
                label: feeds.find(f => f.uri === 'following')?.displayName ?? 'Following',
            },
        ]
        for (const feed of pinnedCustomFeeds) {
            tabs.push({ id: feed.uri, label: feed.displayName })
        }
        return tabs
    }, [selectedProtocol, mackinacUnifiedTabs, feeds, pinnedCustomFeeds])

    const activeTabId = selectedFeedUri ?? 'following'

    // Auto-fallback: if the active tab is not in the visible tab list, navigate to the first
    // available tab. This handles the case where the user unpins the currently-active tab in
    // Feed Settings (or Following is moved and the previously-persisted selectedFeedUri is gone).
    // For Mastodon this doubles as the guarded live-hide reaction: hiding a tab on the Feeds
    // page shrinks `unifiedTabs` and this effect redirects away immediately — but only once
    // hydrated + authoritative, so a transient lists error (which also shrinks unifiedTabs to
    // just the base tabs) can't wipe a valid list-tab selection.
    useEffect(() => {
        if (unifiedTabs.length === 0) return
        if (isMastodonProtocol && (!mastodonSelection.isHydrated || !isFeedsReady)) return
        const isActivePresent = unifiedTabs.some(t => t.id === activeTabId)
        if (isActivePresent) return

        const first = unifiedTabs[0]
        setSelectedFeedUri(first.id === 'following' ? undefined : first.id)
    }, [
        unifiedTabs,
        activeTabId,
        isMastodonProtocol,
        mastodonSelection.isHydrated,
        isFeedsReady,
        setSelectedFeedUri,
    ])

    const isMixedMode =
        selectedProtocol === 'bluesky' &&
        isOnFollowingFeed &&
        !isCrossProtocol &&
        selectedMixFeeds.length > 0

    const {
        posts: mixedPosts,
        isFetching: isMixedFetching,
        isFetchingMore: isMixedFetchingMore,
        hasMore: mixedHasMore,
        loadMore: loadMoreMixed,
    } = useMixedTimeline({
        followingPosts: standardTimeline.posts,
        pinnedCustomFeeds: selectedMixFeeds,
        enabled: isMixedMode,
        followingHasMore: standardTimeline.isLoading || standardTimeline.hasNextPage,
        followingPageCount: standardTimeline.pageCount,
        followingDataUpdatedAt: standardTimeline.dataUpdatedAt,
        followingIsFetching: standardTimeline.isFetching,
    })

    // Followed-hashtag attribution applies only to the Mastodon Following
    // timeline — mutually exclusive with isMixedMode (which is Bluesky-only).
    const isMastodonAttributionMode = isMastodonProtocol && isOnFollowingFeed && !isCrossProtocol

    const { posts: attributedMastodonPosts, isFetching: isAttributionFetching } =
        useMastodonHashtagAttribution({
            posts: standardTimeline.posts,
            enabled: isMastodonAttributionMode,
        })

    const {
        posts: rawPosts,
        queryKey,
        isLoading,
        isFetching: isStandardFetching,
        isFetchingNextPage: isFetchingTimelinePage,
        hasNextPage: timelineHasNextPage,
        fetchNextPage: fetchNextTimelinePage,
        error,
        refetch,
    } = isCrossProtocol ? crossTimeline : standardTimeline

    // Once Following runs out, keep paging the mixed-in feeds (a follows-nobody user, or
    // the end of Following) — the list only calls onLoadMore while hasNextPage is true.
    const hasNextPage = timelineHasNextPage || (isMixedMode && mixedHasMore)
    const isFetchingNextPage = isFetchingTimelinePage || (isMixedMode && isMixedFetchingMore)
    const fetchNextPage = useCallback(() => {
        if (timelineHasNextPage || !isMixedMode) return fetchNextTimelinePage()
        loadMoreMixed()
    }, [timelineHasNextPage, isMixedMode, fetchNextTimelinePage, loadMoreMixed])

    const posts = isMixedMode
        ? mixedPosts
        : isMastodonAttributionMode
          ? attributedMastodonPosts
          : rawPosts

    const isFetching =
        isStandardFetching ||
        (isMixedMode && isMixedFetching) ||
        (isMastodonAttributionMode && isAttributionFetching)

    const { toggleLike, toggleRepost, toggleBookmark, removePost } = usePostEngagement(
        undefined,
        undefined,
        { queryKey }
    )

    const { replyContext, handleReply, closeReply } = useReplyModal()
    const { quoteContext, handleQuote, closeQuote } = useQuoteModal()

    const handleTabChange = useCallback(
        (id: string) => {
            setSelectedFeedUri(id === 'following' ? undefined : id)
            window.scrollTo({ top: 0 })
        },
        [setSelectedFeedUri]
    )

    const handleRefresh = useCallback(() => {
        window.scrollTo({ top: 0 })
        setTimeout(() => queryClient.resetQueries({ queryKey }), 0)
    }, [queryClient, queryKey])

    const handlePullRefresh = useCallback(async () => {
        await refetch()
    }, [refetch])

    const newestPostAt = isCrossProtocol ? undefined : standardTimeline.newestPostAt

    const { hasNewPosts, dismiss: dismissNewPosts } = useNewPostsDetection({
        currentNewestAt: newestPostAt,
        feedUri: selectedFeedUri,
        enabled: !isCrossProtocol,
    })

    const handleNewPostsPillClick = useCallback(() => {
        dismissNewPosts()
        handleRefresh()
    }, [dismissNewPosts, handleRefresh])

    useEffect(() => {
        const handleHomeRetap = () => handleRefresh()
        window.addEventListener('mackinac:home-retap', handleHomeRetap)
        return () => window.removeEventListener('mackinac:home-retap', handleHomeRetap)
    }, [handleRefresh])

    const handleDelete = useCallback((post: Post) => removePost.mutate(post), [removePost])

    const handleAuthorClick = useCallback((id: string) => navigate(`/profile/${id}`), [navigate])

    const handlePostClick = useCallback(
        (post: Post) => {
            if (post.protocol === 'nostr') {
                const hexId = post.uri?.startsWith('nostr:') ? post.uri.slice(6) : post.uri
                if (hexId) void navigate(`/nostr/event/${hexId}`)
            } else if (post.isActivityPub) {
                const route = buildPostRouteFromUri(
                    post.uri,
                    post.author?.handle,
                    post.mastodonStatusId
                )
                if (route) void navigate(route)
            } else if (post.author?.handle && post.rkey) {
                void navigate(`/profile/${post.author.handle}/post/${post.rkey}`)
            }
        },
        [navigate]
    )

    const hasSession = !!adapterSession

    const protocolDisplayName =
        selectedProtocol === 'pixelfed'
            ? 'Pixelfed'
            : selectedProtocol === 'mastodon'
              ? 'Mastodon'
              : 'Bluesky'

    // Slide header behind the fixed app header (h-16 = 4rem) when scrolling down
    const headerHidden = scrollDirection === 'down'

    // Measure the inner feed-selector header height so the pill can sit below it,
    // without hardcoding its height.
    const innerHeaderRef = useRef<HTMLDivElement>(null)
    const [innerHeaderHeight, setInnerHeaderHeight] = useState(0)
    useLayoutEffect(() => {
        if (innerHeaderRef.current) {
            setInnerHeaderHeight(innerHeaderRef.current.offsetHeight)
        }
    }, [])

    const activeTabRef = useRef<HTMLButtonElement>(null)
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        const el = activeTabRef.current
        const container = scrollContainerRef.current
        if (!el || !container) return
        const SCROLL_PADDING = 16
        const elRect = el.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()
        const elLeft = elRect.left - containerRect.left + container.scrollLeft
        const elRight = elRect.right - containerRect.left + container.scrollLeft
        const containerScrollLeft = container.scrollLeft
        const containerWidth = container.offsetWidth
        if (elLeft < containerScrollLeft + SCROLL_PADDING) {
            container.scrollTo?.({ left: elLeft - SCROLL_PADDING, behavior: 'smooth' })
        } else if (elRight > containerScrollLeft + containerWidth - SCROLL_PADDING) {
            container.scrollTo?.({
                left: elRight - containerWidth + SCROLL_PADDING,
                behavior: 'smooth',
            })
        }
    }, [selectedFeedUri])

    // When the inner header is visible, translate the pill down to clear it.
    // When hidden (scroll down), no extra offset — pill sits just below the fixed app header.
    const pillTranslateY = headerHidden ? 0 : innerHeaderHeight

    if (!hasSession) {
        return (
            <div className="max-w-2xl mx-auto">
                <div className="p-8 text-center">
                    <h1 className="text-2xl font-semibold text-text-primary mb-4">
                        Welcome to Mackinac
                    </h1>
                    <p className="text-text-secondary">
                        Sign in with {protocolDisplayName} to see your timeline
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="relative max-w-2xl mx-auto">
            <PullToRefresh onRefresh={handlePullRefresh} disabled={isFetching}>
                <div
                    ref={innerHeaderRef}
                    className={`sticky top-16 z-30 bg-white/95 dark:bg-black/80 backdrop-blur-xl border-b border-border-light dark:border-border-dark transition-transform duration-300 ${headerHidden ? '-translate-y-[calc(100%+4rem)]' : 'translate-y-0'}`}
                >
                    {isCrossProtocol ? (
                        <div className="flex items-center justify-between px-4 py-1 border-b border-border-light dark:border-border-dark">
                            <button
                                className="px-3 py-1.5 rounded-full bg-primary/10 border border-primary/30 text-primary text-sm font-semibold"
                                aria-pressed={true}
                            >
                                Social Web
                            </button>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={handleRefresh}
                                    disabled={isFetching}
                                    className="p-2 rounded-full hover:bg-surface-light dark:hover:bg-surface-dark text-text-secondary-light dark:text-text-secondary-dark transition-colors disabled:opacity-50"
                                    title="Refresh"
                                >
                                    <RefreshCw
                                        className={`w-5 h-5 ${isFetching ? 'animate-spin' : ''}`}
                                    />
                                </button>
                                <button
                                    onClick={() => setSettingsOpen(true)}
                                    aria-label="Cross-protocol settings"
                                    className="p-1.5 rounded-full hover:bg-surface-light dark:hover:bg-surface-dark text-text-secondary-light dark:text-text-secondary-dark transition-colors"
                                >
                                    <Settings className="w-4 h-4" aria-hidden="true" />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center px-2">
                            <div
                                ref={scrollContainerRef}
                                className="flex flex-1 overflow-x-auto scrollbar-hide"
                            >
                                {unifiedTabs.map(tab => {
                                    const isActive = tab.id === activeTabId
                                    return (
                                        <button
                                            key={tab.id}
                                            ref={isActive ? activeTabRef : null}
                                            onClick={() => handleTabChange(tab.id)}
                                            className={`relative shrink-0 px-4 pb-2.5 pt-3 text-sm whitespace-nowrap transition-colors focus:outline-none ${
                                                isActive
                                                    ? 'font-semibold text-text-primary'
                                                    : 'font-normal text-text-secondary hover:text-text-primary'
                                            }`}
                                        >
                                            {tab.label}
                                            {isActive && (
                                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                                            )}
                                        </button>
                                    )
                                })}
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                                {(selectedProtocol === 'bluesky' ||
                                    (selectedProtocol === 'mastodon' && !!mastodonSession)) && (
                                    <button
                                        onClick={() => navigate('/feeds')}
                                        title={strings.feeds.manageFeeds}
                                        aria-label={strings.feeds.manageFeeds}
                                        className="p-1.5 rounded-full transition-colors text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-light dark:hover:bg-surface-dark"
                                    >
                                        <SlidersHorizontal className="w-4 h-4" aria-hidden="true" />
                                    </button>
                                )}
                                <button
                                    onClick={handleRefresh}
                                    disabled={isFetching}
                                    className="p-2 rounded-full hover:bg-surface-light dark:hover:bg-surface-dark text-text-secondary-light dark:text-text-secondary-dark transition-colors disabled:opacity-50"
                                    title="Refresh"
                                >
                                    <RefreshCw
                                        className={`w-5 h-5 ${isFetching ? 'animate-spin' : ''}`}
                                    />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                {hasNewPosts && !isAtTop && scrollDirection !== 'down' && (
                    <NewPostsPill
                        onClick={handleNewPostsPillClick}
                        onDismiss={dismissNewPosts}
                        translateY={pillTranslateY}
                    />
                )}
                <SwipeableTabContent
                    tabs={unifiedTabs}
                    activeId={activeTabId}
                    onChange={handleTabChange}
                >
                    {unifiedTabs.map(tab => {
                        if (tab.id === 'following') {
                            return (
                                <PostList
                                    key={
                                        isCrossProtocol
                                            ? 'cross-protocol'
                                            : `${activeIdentifier}:following`
                                    }
                                    posts={posts}
                                    isLoading={isLoading}
                                    isVisible={isVisible}
                                    isFetchingNextPage={isFetchingNextPage}
                                    hasNextPage={hasNextPage}
                                    onLoadMore={fetchNextPage}
                                    isOwnProfile={false}
                                    currentUserDid={session?.did}
                                    currentMastodonHandle={
                                        currentMastodonHandle ?? currentPixelfedHandle
                                    }
                                    onLike={toggleLike}
                                    onRepost={toggleRepost}
                                    onQuote={handleQuote}
                                    onDelete={handleDelete}
                                    onReply={handleReply}
                                    onBookmark={
                                        adapter.capabilities.bookmarks ? toggleBookmark : undefined
                                    }
                                    onAuthorClick={handleAuthorClick}
                                    onPostClick={handlePostClick}
                                    onRetry={refetch}
                                    error={error}
                                />
                            )
                        }
                        // Custom feed tab
                        return (
                            <PostList
                                key={`${activeIdentifier}:${tab.id}`}
                                posts={posts}
                                isLoading={isLoading}
                                isVisible={isVisible}
                                isFetchingNextPage={isFetchingNextPage}
                                hasNextPage={hasNextPage}
                                onLoadMore={fetchNextPage}
                                isOwnProfile={false}
                                currentUserDid={session?.did}
                                currentMastodonHandle={
                                    currentMastodonHandle ?? currentPixelfedHandle
                                }
                                onLike={toggleLike}
                                onRepost={toggleRepost}
                                onQuote={handleQuote}
                                onDelete={handleDelete}
                                onReply={handleReply}
                                onBookmark={
                                    adapter.capabilities.bookmarks ? toggleBookmark : undefined
                                }
                                onAuthorClick={handleAuthorClick}
                                onPostClick={handlePostClick}
                                onRetry={refetch}
                                error={error}
                            />
                        )
                    })}
                </SwipeableTabContent>
            </PullToRefresh>

            {/* Persistent live region — announces new posts pill to screen readers */}
            <div aria-live="polite" aria-atomic="true" className="sr-only">
                {hasNewPosts ? 'New posts available' : ''}
            </div>

            {replyContext && (
                <ReplyModal
                    isOpen={!!replyContext}
                    onClose={closeReply}
                    replyContext={replyContext}
                />
            )}

            {quoteContext && (
                <QuoteModal
                    isOpen={!!quoteContext}
                    onClose={closeQuote}
                    quoteContext={quoteContext}
                />
            )}

            {settingsOpen && <CrossProtocolSettings onClose={() => setSettingsOpen(false)} />}

            <aside className="hidden lg:block absolute top-0 bottom-0 left-full">
                <div className="sticky top-20 pl-6 w-56">
                    <TrendingSidebar />
                </div>
            </aside>
        </div>
    )
}
